import { isAppInFront, onAppForeground } from '@/lib/app-presence.js';

/**
 * How many thumbnails may download and draw at the same time. A grid of fifty decks would otherwise start
 * fifty Office renderers on its first paint; two keep the page responsive while the rest wait their turn.
 */
export const MAX_CONCURRENT_THUMBNAILS = 2;

let running = 0;
const waiting: Array<() => void> = [];

/** Wait for a free slot. A slot freed by a finishing task is handed straight to the next waiter. */
async function acquireSlot(): Promise<void> {
  if (running < MAX_CONCURRENT_THUMBNAILS) {
    running += 1;
    return;
  }
  await new Promise<void>((resolve) => waiting.push(resolve));
}

/** Hand the slot to the next waiter, or give it back when nobody is waiting. */
function releaseSlot(): void {
  const next = waiting.shift();
  if (next) next();
  else running -= 1;
}

/**
 * Wait until the app is in front of the user. A hidden page, or a tab Teams keeps suspended, starts no new
 * download or draw; the queue picks up again when the app comes back.
 */
export function untilAppInFront(): Promise<void> {
  if (isAppInFront()) return Promise.resolve();
  return new Promise((resolve) => {
    const stopListening = onAppForeground(() => {
      stopListening();
      resolve();
    });
  });
}

/**
 * Run one thumbnail task once a slot is free and the app is in front. A task that fails frees its slot
 * like one that succeeds.
 */
export async function runQueued<T>(task: () => Promise<T>): Promise<T> {
  await acquireSlot();
  try {
    await untilAppInFront();
    return await task();
  } finally {
    releaseSlot();
  }
}
